import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ContentChild,
  Inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { Icons } from '@app/shared/constants';
import { Entity } from '@app/shared/models/models.interface';
import { ButtonIconComponent } from '@components/buttons/button-icon/button-icon.component';
import { ButtonToggleGroupComponent } from '@components/buttons/button-toggle-group/button-toggle-group.component';
import { CheckboxComponent } from '@components/form-controls/checkbox/checkbox.component';
import { InputSearchComponent } from '@components/form-controls/inputs/input-search/input-search.component';
import { SimpleSelectComponent } from '@components/form-controls/simple-select/simple-select.component';
import { Subject } from 'rxjs';
import { DataSource } from './datasource/datasource.component';
import { MultiselectStatus } from './directives/datatable-multiselect.directive';
import { FilterBuilderDirective } from './directives/filter-builder.directive';
import { EntitySelectionModel } from './entitySelectionModel';
import { DataTableState } from './state/state.model';
import { StateService } from './state/state.service';

export const DataTableView = {
  GRID: 'grid',
  CARD: 'card',
  LIST: 'list'
};

@Component({
  selector: 'vfs-data-table-new',
  templateUrl: './data-table-new.component.html',
  styleUrls: ['./data-table-new.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DataTableNewComponent<T extends Entity> implements OnInit, AfterViewInit, AfterViewChecked {
  @ViewChild('searchInput') searchInput: InputSearchComponent;
  @ViewChild('filtersButton') filtersButton: ButtonIconComponent;
  @ViewChild('switchViewToggle') switchViewToggle: ButtonToggleGroupComponent;
  @ViewChild('multiselect') multiselect: CheckboxComponent;
  @ViewChild('sortSelect') sortSelect: SimpleSelectComponent;
  @ViewChild('sortButton') sortButton: ButtonIconComponent;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ContentChild('cardTemplate') cardTemplate: TemplateRef<any>;
  @ContentChild('listBarTemplate') listBarTemplate: TemplateRef<any>;
  @ContentChild('listTemplate') listTemplate: TemplateRef<any>;
  @ContentChild('multiselectTemplate') multiselectTemplate: TemplateRef<any>;
  @ContentChild('multiselectInactiveTemplate') multiselectInactiveTemplate: TemplateRef<any>;
  @ContentChild('actionsTemplate') actionsTemplate: TemplateRef<any>;
  @ContentChild(FilterBuilderDirective) filterBuilderDirective: FilterBuilderDirective;

  @Input() stateId: string;
  @Input() dataSource: DataSource<T>;
  @Input() columns: string[];
  @Input() selection: EntitySelectionModel<T>;
  @Input() view: string;
  @Input() emptyState: string;
  @Input() emptyStateHint = '';
  @Input() emptyStateDetail = false;
  @Input() inputFullWidth = false;
  @Input() stateChangeSubject$ = new Subject<DataTableState>();

  // Directives flags
  withSort = false;
  withSwitchView = false;
  withSearch = false;
  withFilters = false;
  withMultiselect = false;
  withPaginator = false;
  withActions = false;
  withInfiniteScroll = false;

  sortIcon = Icons.SORT_ASC;
  currentView = DataTableView.GRID;
  multiselectStatus: MultiselectStatus;
  areFiltersApplied = false;
  scrollSubject$: Subject<void>;

  constructor(@Inject('StateService') private stateService: StateService) {}

  ngOnInit() {
    this.stateService.init(this.stateId);
    this.currentView = this.view ? this.validateView(this.view) : DataTableView.GRID;
  }

  ngAfterViewInit() {
    if (this.withInfiniteScroll) {
      this.scrollSubject$ = new Subject();
    }
  }

  ngAfterViewChecked() {
    if (this.multiselect && this.multiselectStatus) {
      this.multiselect.checked = this.multiselectStatus.active;
    }
  }

  dataSourceContainsData(): boolean {
    return this.dataSource.data && this.dataSource.data.length > 0;
  }

  hasActionbar(): boolean {
    return this.withSwitchView || this.withSearch || this.withFilters || this.withMultiselect;
  }

  selectedView() {
    return this.currentView !== 'grid' ? 'flex' : 'none';
  }

  getOpaqueConditions(): boolean {
    return this.emptyStateDetail || this.emptyState != null;
  }

  private validateView(view: string): string {
    switch (view) {
      case DataTableView.GRID:
      case DataTableView.LIST:
      case DataTableView.CARD:
        return view;
      default:
        throw Error('Invalid specified view: ' + view);
    }
  }

  public onScroll() {
    this.scrollSubject$.next();
  }
}
